import type { AnimalListItem, EstimateItem, MapContext, MarkerSnapshot } from '../../api/demo-client.js';

/**
 * Cache do mapa por dia (M03-FP), SOMENTE em memória da tela do cliente.
 *
 * - chave: cliente + fazenda + instante do dia histórico, ou cliente + fazenda
 *   + `current` para Hoje — o instante de Hoje avança a cada atualização dentro
 *   da MESMA entrada, sem criar uma por consulta; nada de outra chave vaza;
 * - valores separados: marcadores (com ETag), contexto cartográfico e
 *   metadados carregados sob demanda, por animal ou pela lista completa;
 * - no máximo `limit` dias; gravar um dia o torna o mais novo e o mais antigo
 *   sai ao exceder;
 * - uma promessa em voo por (tipo, chave): chamadas repetidas compartilham;
 * - sem storage do navegador: some com reload, logout ou saída da tela.
 */

export interface DayDetails {
  readonly items: readonly AnimalListItem[];
  readonly byKey: ReadonlyMap<string, EstimateItem>;
  readonly complete: boolean;
}

export interface DayEntry {
  readonly markers?: MarkerSnapshot;
  readonly context?: MapContext;
  readonly details?: DayDetails;
}

export function dayKey(tenantId: string, farmId: string, asOfMs: number): string {
  return `${tenantId}/${farmId}/${asOfMs}`;
}

/** Entrada estável de Hoje: o instante de referência muda dentro dela. */
export function currentKey(tenantId: string, farmId: string): string {
  return `${tenantId}/${farmId}/current`;
}

/** Mesmos alvos e valores: só o instante de referência pode ter mudado. */
export function sameMarkers(a: MarkerSnapshot | undefined, b: MarkerSnapshot): boolean {
  return a !== undefined && a.markers.length === b.markers.length && JSON.stringify(a.markers) === JSON.stringify(b.markers);
}

/** Mesma geometria e nomes, ignorando o instante resolvido. */
export function sameContext(a: MapContext | undefined, b: MapContext): boolean {
  return a !== undefined && JSON.stringify({ ...a, resolvedAsOfMs: 0 }) === JSON.stringify({ ...b, resolvedAsOfMs: 0 });
}

export class DayCache {
  private readonly entries = new Map<string, DayEntry>();
  private readonly inflight = new Map<string, Promise<unknown>>();

  constructor(private readonly limit = 32) {}

  get(key: string): DayEntry | undefined {
    return this.entries.get(key);
  }

  /** Mescla no dia e o torna o mais recente; remove o mais antigo acima do limite. */
  update(key: string, patch: DayEntry): DayEntry {
    const entry = { ...this.entries.get(key), ...patch };
    this.entries.delete(key);
    this.entries.set(key, entry);
    while (this.entries.size > this.limit) this.entries.delete(this.entries.keys().next().value!);
    return entry;
  }

  /** Mesma promessa para o mesmo (tipo, chave) enquanto estiver em voo. */
  shared<T>(kind: string, key: string, load: () => Promise<T>): Promise<T> {
    const id = `${kind}:${key}`;
    const running = this.inflight.get(id) as Promise<T> | undefined;
    if (running !== undefined) return running;
    const promise = load().finally(() => this.inflight.delete(id));
    this.inflight.set(id, promise);
    return promise;
  }

  /** Acesso negado ou recurso fora do escopo: some tudo do cliente (ou só da fazenda). */
  forget(tenantId: string, farmId?: string): void {
    const prefix = farmId === undefined ? `${tenantId}/` : `${tenantId}/${farmId}/`;
    for (const key of [...this.entries.keys()]) if (key.startsWith(prefix)) this.entries.delete(key);
  }

  clear(): void {
    this.entries.clear();
  }

  get size(): number {
    return this.entries.size;
  }
}
